import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Send, ChevronLeft, Users, Search,
  AlertCircle, User, RefreshCw, Check, CheckCheck,
  Megaphone, Circle
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminChat = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [view, setView] = useState('conversations'); // conversations, chat, users
  const messagesEndRef = useRef(null);

  const fetchAdminUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/chat/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch admin users:', err);
    }
  }, [token]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/chat/messages/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        // Scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchAdminUsers();
    fetchConversations();
  }, [fetchAdminUsers, fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      // Poll for new messages every 10 seconds
      const interval = setInterval(() => {
        fetchMessages(selectedConversation.id);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation, fetchMessages]);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setView('chat');
  };

  const handleStartChat = async (recipient) => {
    // Send empty message to create conversation, then navigate
    try {
      const response = await fetch(`${API_URL}/api/admin/chat/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: recipient.id,
          content: `Started a conversation`,
          message_type: 'direct'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        await fetchConversations();
        const newConv = { id: data.conversation_id, participants: [recipient] };
        setSelectedConversation(newConv);
        setView('chat');
      }
    } catch (err) {
      console.error('Failed to start chat:', err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    try {
      setSending(true);
      const otherParticipant = selectedConversation.participants?.find(p => p.id !== user?.id);
      
      const response = await fetch(`${API_URL}/api/admin/chat/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: otherParticipant?.id,
          content: newMessage,
          message_type: selectedConversation.type === 'broadcast' ? 'broadcast' : 'direct'
        })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(selectedConversation.id);
        fetchConversations();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = async () => {
    if (!newMessage.trim() || sending) return;
    
    try {
      setSending(true);
      const response = await fetch(`${API_URL}/api/admin/chat/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newMessage,
          message_type: 'broadcast'
        })
      });

      if (response.ok) {
        setNewMessage('');
        fetchConversations();
        alert('Announcement sent to all team members!');
      }
    } catch (err) {
      console.error('Failed to broadcast:', err);
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin')} className="admin-btn primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin')} className="back-btn">
              <ChevronLeft size={20} />
              Back
            </button>
            <h1>Team Chat</h1>
            <p className="admin-subtitle">Internal messaging for admin staff</p>
          </div>
          <div className="admin-header-actions">
            <button onClick={fetchConversations} className="admin-btn secondary">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

      {/* Broadcast Section */}
      <div className="broadcast-section">
        <div className="broadcast-header">
          <Megaphone size={20} />
          <span>Team Announcement</span>
        </div>
        <div className="broadcast-form">
          <input
            type="text"
            value={view === 'broadcast' ? newMessage : ''}
            onChange={(e) => { setView('broadcast'); setNewMessage(e.target.value); }}
            placeholder="Send announcement to all team members..."
            className="broadcast-input"
          />
          <button 
            onClick={handleBroadcast}
            disabled={sending || !newMessage.trim() || view !== 'broadcast'}
            className="admin-btn primary"
          >
            <Send size={16} />
            Broadcast
          </button>
        </div>
      </div>

      <div className="chat-layout">
        {/* Sidebar - Users & Conversations */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-tabs">
            <button 
              className={`sidebar-tab ${view === 'conversations' || view === 'chat' ? 'active' : ''}`}
              onClick={() => setView('conversations')}
            >
              <MessageSquare size={16} />
              Chats
            </button>
            <button 
              className={`sidebar-tab ${view === 'users' ? 'active' : ''}`}
              onClick={() => setView('users')}
            >
              <Users size={16} />
              Team
            </button>
          </div>

          {loading ? (
            <div className="chat-loading">
              <RefreshCw className="spin" size={24} />
            </div>
          ) : view === 'users' ? (
            <div className="users-list">
              {adminUsers.filter(u => !u.is_current_user).map(adminUser => (
                <div 
                  key={adminUser.id}
                  className="user-item"
                  onClick={() => handleStartChat(adminUser)}
                >
                  <div className="user-avatar">
                    <User size={18} />
                    <span className="online-dot"></span>
                  </div>
                  <div className="user-info">
                    <span className="user-name">{adminUser.first_name} {adminUser.last_name}</span>
                    <span className="user-role">{adminUser.role}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="conversations-list">
              {conversations.length === 0 ? (
                <div className="no-conversations">
                  <MessageSquare size={32} />
                  <p>No conversations yet</p>
                  <p className="hint">Click "Team" to start a chat</p>
                </div>
              ) : (
                conversations.map(conv => {
                  const otherParticipant = conv.participants?.find(p => p.id !== user?.id);
                  const displayName = conv.type === 'broadcast' 
                    ? 'Team Announcements' 
                    : `${otherParticipant?.first_name || ''} ${otherParticipant?.last_name || ''}`.trim() || 'Unknown';
                  
                  return (
                    <div 
                      key={conv.id}
                      className={`conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''} ${conv.unread_count > 0 ? 'unread' : ''}`}
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <div className="conv-avatar">
                        {conv.type === 'broadcast' ? <Megaphone size={18} /> : <User size={18} />}
                      </div>
                      <div className="conv-info">
                        <div className="conv-header">
                          <span className="conv-name">{displayName}</span>
                          {conv.last_message && (
                            <span className="conv-time">{formatTime(conv.last_message.created_at)}</span>
                          )}
                        </div>
                        {conv.last_message && (
                          <p className="conv-preview">{conv.last_message.content}</p>
                        )}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="unread-badge">{conv.unread_count}</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className="chat-main">
          {selectedConversation ? (
            <>
              <div className="chat-header">
                <button className="mobile-back" onClick={() => setSelectedConversation(null)}>
                  <ChevronLeft size={20} />
                </button>
                <div className="chat-recipient">
                  {selectedConversation.type === 'broadcast' ? (
                    <>
                      <Megaphone size={20} />
                      <span>Team Announcements</span>
                    </>
                  ) : (
                    <>
                      <User size={20} />
                      <span>
                        {selectedConversation.participants?.find(p => p.id !== user?.id)?.first_name || 'Chat'}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="chat-messages">
                {messages.map(msg => (
                  <div 
                    key={msg.id}
                    className={`message ${msg.sender_id === user?.id ? 'sent' : 'received'}`}
                  >
                    <div className="message-content">
                      <p>{msg.content}</p>
                      <span className="message-time">
                        {formatTime(msg.created_at)}
                        {msg.sender_id === user?.id && (
                          msg.read_by?.length > 1 ? <CheckCheck size={12} /> : <Check size={12} />
                        )}
                      </span>
                    </div>
                    {msg.sender_id !== user?.id && (
                      <span className="message-sender">{msg.sender_name}</span>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <form className="chat-input-form" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  value={view === 'chat' ? newMessage : ''}
                  onChange={(e) => { setView('chat'); setNewMessage(e.target.value); }}
                  placeholder="Type a message..."
                  className="chat-input"
                />
                <button type="submit" disabled={sending || !newMessage.trim()} className="send-btn">
                  <Send size={20} />
                </button>
              </form>
            </>
          ) : (
            <div className="chat-empty">
              <MessageSquare size={64} />
              <h3>Select a conversation</h3>
              <p>Choose a chat from the sidebar or start a new one</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default AdminChat;
